import React, { useContext, useState } from "react"
import PropTypes from "prop-types"
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import Part from "./Part"
import { formatValue } from "react-currency-input-field"
import { getCurrency } from "utils/functions"
import AuthContext from "context/auth/authContext"
import i18n from "i18n"

const Chapter = ({
  chapter,
  typeBudget,
  fetchChapter,
  width,
  selectedParts,
  setSelectedParts,
  priceField = "clientPrice",
  level = 0,
}) => {
  const [subChapters, setSubChapters] = useState([])
  const [parts, setParts] = useState([])
  const { user } = useContext(AuthContext)
  const [expanded, setExpanded] = useState(false)
  const [fetched, setFetched] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleChange = async (event, isExpanded) => {
    setExpanded(isExpanded)
    if (isExpanded && !fetched) {
      setLoading(true)
      const response = await fetchChapter({
        projectId: chapter.projectId,
        parentId: chapter.chapterId,
        typeBudget: typeBudget,
      })
      setSubChapters(response.chapters)
      setParts(response.parts)
      setFetched(true)
      setLoading(false)
    }
  }

  const format = value => {
    return formatValue({
      value: value.toFixed(2),
      groupSeparator: ".",
      decimalSeparator: ",",
      suffix: " " + getCurrency(user["custom:currency"]),
      decimalScale: 2,
      fixedDecimalScale: true,
    })
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      sx={{
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "4px !important",
        mb: 1,
        "&:before": { display: "none" },
        "&.Mui-expanded": {
          mb: 1,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: level === 0 ? "grey.100" : "grey.50",
          borderRadius: "4px",
          "&.Mui-expanded": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            pr: 2,
          }}
        >
          <Typography sx={{ fontWeight: level === 0 ? 600 : 400 }}>
            {chapter.name}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {subChapters.length > 0 && (
              <Box sx={{ p: 1, pb: 0 }}>
                {subChapters.map((subchapter, index) => (
                  <Chapter
                    chapter={subchapter}
                    key={index}
                    typeBudget={typeBudget}
                    fetchChapter={fetchChapter}
                    width={width - 1}
                    selectedParts={selectedParts}
                    setSelectedParts={setSelectedParts}
                    priceField={priceField}
                    level={level + 1}
                  />
                ))}
              </Box>
            )}
            {parts.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell
                        padding="none"
                        sx={{ width: 40, pl: 1, py: 0.5, px: 1 }}
                      />
                      <TableCell
                        sx={{ fontWeight: 600, width: 50, py: 0.5, px: 1 }}
                      >
                        U
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5, px: 1 }}>
                        {i18n.t("Descripción")}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, width: 80, py: 0.5, px: 1 }}
                      >
                        {i18n.t("Medición")}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, width: 100, py: 0.5, px: 1 }}
                      >
                        {getCurrency(user["custom:currency"])}
                        /u
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, width: 100, py: 0.5, px: 1 }}
                      >
                        {i18n.t("Total")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parts.map((part, index) => (
                      <Part
                        part={part}
                        selectedParts={selectedParts}
                        setSelectedParts={setSelectedParts}
                        priceField={priceField}
                        key={index}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

Chapter.propTypes = {
  chapter: PropTypes.object.isRequired,
  typeBudget: PropTypes.string.isRequired,
  fetchChapter: PropTypes.func.isRequired,
  width: PropTypes.number.isRequired,
  selectedParts: PropTypes.array.isRequired,
  setSelectedParts: PropTypes.func.isRequired,
  priceField: PropTypes.string,
  level: PropTypes.number,
}

export default Chapter
